import React, { useEffect, useMemo, useState } from "react";
import { Col, Form, Input, Modal, Row, Select, Space, Switch, Typography, message } from "antd";
import { financeService } from "../../services/financeService";
import type { GlAccount } from "../../types";

const { Text } = Typography;

const ACCOUNT_TYPES: GlAccount["type"][] = ["asset", "liability", "equity", "income", "expense"];

/**
 * Standard accounting follows the type: assets and expenses are debit-natured,
 * everything else is credit-natured. Deriving this avoids a perfectly valid but
 * nonsensical combination such as an income account with a debit balance.
 */
const NORMAL_BALANCE: Record<GlAccount["type"], "debit" | "credit"> = {
  asset: "debit",
  expense: "debit",
  liability: "credit",
  equity: "credit",
  income: "credit",
};

type AccountFormValues = Partial<GlAccount> & {
  code?: string;
  name?: string;
  normal_balance?: "debit" | "credit";
  parent_id?: string | null;
  is_posting?: boolean;
  is_active?: boolean;
};

interface Props {
  open: boolean;
  /** Full account list, used for the parent-header picker and duplicate checks. */
  accounts: GlAccount[];
  editing?: GlAccount | null;
  /**
   * Set when the modal is opened from a voucher's Ledger account field. The
   * account is created for that voucher type, postable and active, so it can be
   * selected immediately. The full chart of accounts stays in Finance Setup.
   */
  quickType?: GlAccount["type"];
  onClose: () => void;
  onSaved: (account: GlAccount, created: boolean) => void;
}

/**
 * One create/edit form for the chart of accounts, shared by Finance Setup and
 * the voucher entry form. Keeping it in one place means a field added here is
 * available everywhere an account can be raised.
 */
const LedgerAccountModal: React.FC<Props> = ({
  open,
  accounts,
  editing,
  quickType,
  onClose,
  onSaved,
}) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const isQuick = !!quickType && !editing;
  const editingId = editing?.id ?? null;
  const watchedType = Form.useWatch("type", form) as GlAccount["type"] | undefined;
  const type = isQuick ? quickType : watchedType;

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    if (editing) {
      form.setFieldsValue({ ...editing });
      return;
    }
    const initialType = quickType || "expense";
    form.setFieldsValue({
      type: initialType,
      normal_balance: NORMAL_BALANCE[initialType],
      is_posting: true,
      is_active: true,
    });
  }, [open, editing, quickType, form]);

  // Normal balance follows the type until the account is saved.
  useEffect(() => {
    if (!open || editing || !type) return;
    form.setFieldValue("normal_balance", NORMAL_BALANCE[type]);
  }, [open, editing, type, form]);

  const parentOptions = useMemo(
    () =>
      accounts
        .filter((a) => !a.is_posting && a.id !== editingId)
        .map((a) => ({ value: a.id, label: `${a.code} — ${a.name}` })),
    [accounts, editingId],
  );

  const submit = async () => {
    let values: AccountFormValues;
    try {
      values = await form.validateFields();
    } catch {
      // Antd already highlights the invalid fields.
      return;
    }
    const accountType = (isQuick ? quickType : values.type) as GlAccount["type"];
    setSaving(true);
    try {
      const saved = editing
        ? await financeService.updateAccount(editing.id, {
            name: String(values.name || "").trim(),
            parent_id: values.parent_id || null,
            is_posting: values.is_posting !== false,
            is_active: values.is_active !== false,
          })
        : await financeService.createAccount({
            code: String(values.code || "").trim(),
            name: String(values.name || "").trim(),
            type: accountType,
            normal_balance: isQuick ? NORMAL_BALANCE[accountType] : values.normal_balance,
            parent_id: values.parent_id || null,
            is_posting: isQuick ? true : values.is_posting !== false,
            is_active: isQuick ? true : values.is_active !== false,
          });
      message.success(editing ? "Ledger account updated" : "Ledger account created");
      onSaved(saved, !editing);
      onClose();
    } catch (e: any) {
      message.error(e?.message || "Failed to save the ledger account");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title={editing ? `Edit ${editing.code}` : "New ledger account"}
      onCancel={onClose}
      onOk={submit}
      okText={editing ? "Save" : "Create"}
      confirmLoading={saving}
      destroyOnClose
      width={560}
    >
      <Form form={form} layout="vertical">
        {!editing && (
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item
                name="code"
                label="Code"
                rules={[
                  { required: true, message: "Required" },
                  {
                    validator: (_, value) =>
                      accounts.some(
                        (a) => a.code === String(value || "").trim() && a.id !== editingId,
                      )
                        ? Promise.reject(new Error("That code is already in use"))
                        : Promise.resolve(),
                  },
                ]}
              >
                <Input placeholder="6900" maxLength={10} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="type" label="Type" rules={[{ required: true }]}>
                <Select
                  disabled={isQuick}
                  options={ACCOUNT_TYPES.map((v) => ({ value: v, label: v }))}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="normal_balance" label="Normal balance" rules={[{ required: true }]}>
                <Select
                  disabled={isQuick}
                  options={[
                    { value: "debit", label: "Debit" },
                    { value: "credit", label: "Credit" },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
        )}
        <Form.Item name="name" label="Name" rules={[{ required: true, message: "Required" }]}>
          <Input placeholder="Staff transport allowance" maxLength={120} />
        </Form.Item>
        <Form.Item name="parent_id" label="Parent (header) account">
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="None"
            options={parentOptions}
          />
        </Form.Item>
        {isQuick ? (
          <Text type="secondary">
            Created as an active, postable {quickType} account and selected in this voucher.
          </Text>
        ) : (
          <Space size={32}>
            <Form.Item name="is_posting" label="Postable" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item name="is_active" label="Active" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Space>
        )}
      </Form>
    </Modal>
  );
};

export default LedgerAccountModal;
